import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ChevronRight,
  Code,
  Cloud,
  Brain,
  Layers,
  Cpu,
  Briefcase,
  Globe,
  Users,
  Award,
} from "lucide-react";

export default function Home() {
  return (
    <div className="w-full text-gray-800 font-sans">

      {/* ================= HERO ================= */}
      <motion.section
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="bg-gradient-to-r from-blue-900 via-blue-800 to-blue-700 text-white py-28 px-6"
      >
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">

          {/* LEFT */}
          <div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-8">
              Enterprise Corporate Training & Technology Consulting
            </h1>

            <p className="text-lg md:text-xl text-blue-100 mb-12 max-w-xl">
              TechX Incorporation partners with global organizations to build
              future-ready engineering teams and deliver enterprise-grade
              technology solutions through consulting-led training and execution.
            </p>

            <div className="flex flex-wrap gap-4">
              <Link
                to="/training"
                className="px-6 py-3 rounded-lg font-semibold text-white
                           bg-white/20 backdrop-blur-md border border-white/30
                           hover:bg-white/30 transition"
              >
                Explore Corporate Training
              </Link>

              <Link
                to="/contact"
                className="px-6 py-3 rounded-lg font-semibold text-blue-900
                           bg-white hover:bg-gray-100 transition"
              >
                Request Consultation
              </Link>
            </div>
          </div>

          {/* RIGHT */}
          <div className="hidden lg:block">
            <div className="space-y-5 mb-8">
              {[
                "Corporate Training & Workforce Upskilling",
                "Technology Consulting & Strategic Advisory",
                "Enterprise IT & Software Engineering",
                "Global Delivery & Multi-Region Engagement Models",
              ].map((item) => (
                <div key={item} className="flex items-start gap-4">
                  <ChevronRight className="text-blue-300 mt-1" size={22} />
                  <p className="text-lg font-medium">{item}</p>
                </div>
              ))}
            </div>

            <p className="text-blue-200 max-w-lg leading-relaxed">
              We operate at the intersection of learning, consulting, and
              delivery—enabling enterprises to scale technology capabilities
              with confidence.
            </p>
          </div>
        </div>
      </motion.section>

      {/* ================= CORPORATE TRAINING ================= */}
      <section className="py-28 px-6 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-10">
            Corporate Training & Enterprise Upskilling
          </h2>

          <p className="text-gray-600 max-w-4xl mb-6 leading-relaxed">
            Our corporate training programs are built with a consulting mindset.
            We focus on enterprise relevance, production-grade practices, and
            real-world implementation—not generic, classroom-based learning.
          </p>

          <p className="text-gray-600 max-w-4xl mb-6 leading-relaxed">
            Training engagements are designed after understanding business
            objectives, team maturity, and existing system landscapes.
          </p>

          <p className="text-gray-600 max-w-4xl mb-20 leading-relaxed">
            Programs are delivered across beginner to advanced levels and are
            aligned with long-term technology roadmaps rather than short-term
            tool adoption.
          </p>

          <div className="grid md:grid-cols-3 gap-10">
            <InfoCard
              icon={Code}
              title="Engineering & Programming"
              desc="Comprehensive training across Java, Python, Node.js, Kotlin, Swift, Dart, TypeScript, and enterprise frameworks such as Spring, Spring Boot, React, Angular, and mobile platforms. Emphasis is placed on architecture, scalability, and maintainability."
            />
            <InfoCard
              icon={Cloud}
              title="Cloud, Data & Platforms"
              desc="Enterprise-focused programs covering AWS, Azure, GCP, Docker, Kubernetes, Apache Spark, Airflow, NiFi, IOMETE, and modern data platforms with a strong focus on deployment, security, and operations."
            />
            <InfoCard
              icon={Brain}
              title="AI, Testing & Enterprise Systems"
              desc="Advanced training across AI-driven applications, test automation frameworks, database technologies, and Oracle enterprise platforms including Visual Builder and VBCS."
            />
          </div>
        </div>
      </section>

      {/* ================= TRUSTED BY ================= */}
      <section className="py-28 px-6 bg-gray-900 text-gray-100">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-10">
            Trusted by Global Enterprises Worldwide
          </h2>

          <p className="text-gray-300 max-w-4xl mx-auto mb-6 leading-relaxed">
            TechX Incorporation has delivered corporate training programs,
            technology consulting, and enterprise enablement initiatives for
            leading global organizations across multiple industries.
          </p>

          <p className="text-gray-400 max-w-4xl mx-auto mb-20 leading-relaxed">
            Our engagements span Fortune enterprises, global system integrators,
            consulting firms, and specialized technology organizations,
            supporting distributed teams across regions and time zones.
          </p>

          <div className="flex flex-wrap justify-center gap-6">
            {[
              "Oracle",
              "Amazon",
              "TCS",
              "Infosys",
              "Cognizant",
              "Wipro",
              "Volkswagen",
              "ATOS",
              "Publicis Sapient",
              "Telstra",
            ].map((client) => (
              <span
                key={client}
                className="px-6 py-3 rounded-full border border-gray-600
                           text-gray-200 font-semibold hover:bg-gray-800 transition"
              >
                {client}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ================= IT SOLUTIONS ================= */}
      <section className="py-28 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-10">
            Enterprise IT Solutions & Software Engineering
          </h2>

          <p className="text-gray-600 max-w-4xl mb-6 leading-relaxed">
            Beyond training, TechX Incorporation delivers end-to-end enterprise
            IT solutions that support complex business workflows and
            large-scale technology ecosystems.
          </p>

          <p className="text-gray-600 max-w-4xl mb-20 leading-relaxed">
            Our delivery spans architecture and design, application development,
            testing, deployment, modernization, and long-term system evolution.
          </p>

          <div className="grid md:grid-cols-3 gap-10">
            <InfoCard
              icon={Layers}
              title="Enterprise Applications"
              desc="Design and development of scalable enterprise platforms, internal systems, and customer-facing applications built using modern architectural principles."
            />
            <InfoCard
              icon={Cloud}
              title="Cloud & Data Platforms"
              desc="Cloud-native infrastructure, data engineering platforms, analytics pipelines, and secure architectures designed for performance, reliability, and compliance."
            />
            <InfoCard
              icon={Cpu}
              title="AI & Intelligent Systems"
              desc="Development, testing, and lifecycle management of AI-powered applications and intelligent enterprise solutions across multiple domains."
            />
          </div>
        </div>
      </section>

      {/* ================= WHY TECHX ================= */}
      <section className="py-28 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-10">
            Why TechX Incorporation
          </h2>

          <p className="text-gray-600 max-w-4xl mx-auto mb-20 leading-relaxed">
            Organizations choose TechX Incorporation not just for training or
            delivery, but for long-term partnership, technical credibility, and
            enterprise-scale execution capabilities.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10">
            <WhyCard
              icon={Briefcase}
              title="Enterprise-First Approach"
              desc="All programs and solutions are designed with governance, security, scalability, and real enterprise constraints in mind."
            />
            <WhyCard
              icon={Users}
              title="Practitioner-Led Delivery"
              desc="Engagements are led by professionals with hands-on industry, consulting, and real-world delivery experience."
            />
            <WhyCard
              icon={Award}
              title="Consulting + Training Model"
              desc="A differentiated model that integrates consulting rigor with structured, outcome-driven enterprise training."
            />
            <WhyCard
              icon={Globe}
              title="Global Engagement Experience"
              desc="Proven track record delivering programs and solutions across global enterprises, regions, and distributed teams."
            />
          </div>
        </div>
      </section>

    </div>
  );
}

/* ================= COMPONENTS ================= */

function InfoCard({ icon: Icon, title, desc }) {
  return (
    <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition">
      <Icon className="text-blue-600 mb-4" size={28} />
      <h3 className="font-semibold text-xl mb-3">{title}</h3>
      <p className="text-gray-600 leading-relaxed">{desc}</p>
    </div>
  );
}

function WhyCard({ icon: Icon, title, desc }) {
  return (
    <div className="bg-white rounded-2xl p-8 text-center shadow-sm hover:shadow-md transition">
      <Icon className="text-blue-600 mx-auto mb-4" size={30} />
      <h4 className="font-semibold text-lg mb-2">{title}</h4>
      <p className="text-gray-600 text-sm leading-relaxed">{desc}</p>
    </div>
  );
}
